import React, { useState, useRef } from 'react';
import { storage } from '../../../firebase/firebaseConfig';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { compressImage } from '../../../utils/imageUtils';

const EquipmentImageUpload = ({ images = [], onChange, maxImages, equipmentId, teamId, userPlan }) => {
  // Determine maxImages based on user plan if not provided
  const effectiveMaxImages = maxImages !== undefined 
    ? maxImages 
    : (userPlan === 'PRO' ? 3 : 1); // Pro: 3 images, Free: 1 image
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const remainingSlots = effectiveMaxImages - images.length;
  const canUploadMore = remainingSlots > 0;

  const handleFileSelect = async (files) => {
    const fileArray = Array.from(files);
    const validFiles = fileArray.filter(file => {
      if (!file.type.startsWith('image/')) {
        alert(`ไฟล์ ${file.name} ไม่ใช่ไฟล์รูปภาพ`);
        return false;
      }
      if (file.size > 5 * 1024 * 1024) {
        alert(`ไฟล์ ${file.name} มีขนาดใหญ่เกินไป (ไม่เกิน 5MB)`);
        return false;
      }
      return true;
    });

    const filesToAdd = validFiles.slice(0, remainingSlots);
    
    if (filesToAdd.length === 0) return;

    setUploading(true);
    setUploadProgress(0);

    try {
      const uploadPromises = filesToAdd.map(async (file, index) => {
        setUploadProgress((index / filesToAdd.length) * 50);
        
        let fileToUpload = file;
        
        // บีบอัดรูปภาพ
        try {
          fileToUpload = await compressImage(file, 1200, 1200, 0.85);
        } catch (compressError) {
          console.warn('Error compressing image, using original:', compressError);
          fileToUpload = file;
        }
        
        setUploadProgress(50 + (index / filesToAdd.length) * 50);
        
        // สร้างชื่อไฟล์ที่ไม่ซ้ำ
        const timestamp = Date.now();
        const randomString = Math.random().toString(36).substring(2, 8);
        const fileExtension = file.name.split('.').pop();
        const fileName = `equipment_${equipmentId || 'new'}_${timestamp}_${index}_${randomString}.${fileExtension}`;
        const storagePath = `equipment/${teamId || 'default'}/${fileName}`;
        
        // อัพโหลดไฟล์
        const storageRef = ref(storage, storagePath);
        await uploadBytes(storageRef, fileToUpload);
        const downloadURL = await getDownloadURL(storageRef);
        
        return {
          url: downloadURL,
          name: file.name,
          size: file.size,
          path: storagePath
        };
      });

      const uploadedImages = await Promise.all(uploadPromises);
      onChange([...images, ...uploadedImages]);
      
      setUploadProgress(100);
      setUploading(false);
    } catch (error) {
      console.error('Error uploading images:', error);
      alert(`เกิดข้อผิดพลาดในการอัพโหลด: ${error.message}`);
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDelete = async (imageToDelete, index) => {
    if (!window.confirm('ยืนยันการลบรูปภาพ?')) return;

    try {
      // ลบจาก storage
      if (imageToDelete.path) {
        const storageRef = ref(storage, imageToDelete.path);
        await deleteObject(storageRef);
      }

      // อัพเดท images
      const newImages = images.filter((_, i) => i !== index);
      onChange(newImages);
    } catch (error) {
      console.error('Error deleting image:', error);
      alert(`เกิดข้อผิดพลาดในการลบ: ${error.message}`);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0] && canUploadMore) {
      handleFileSelect(e.dataTransfer.files);
    }
  };

  return (
    <div className="space-y-4">
      {/* Image Grid */}
      {images.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {images.map((image, index) => (
            <div key={index} className="relative group">
              <img
                src={image.url}
                alt={`Equipment ${index + 1}`}
                className="w-full h-32 object-cover rounded-lg border border-gray-200"
              />
              <button
                onClick={() => handleDelete(image, index)}
                className="absolute top-2 right-2 bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                title="ลบรูปภาพ"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Upload Area */}
      {canUploadMore && (
        <div
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-lg p-6 text-center transition ${
            dragActive
              ? 'border-blue-500 bg-blue-50'
              : 'border-gray-300 hover:border-gray-400'
          } ${uploading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          onClick={() => !uploading && fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*"
            onChange={(e) => handleFileSelect(e.target.files)}
            className="hidden"
            disabled={uploading}
          />
          
          {uploading ? (
            <div>
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
              <p className="text-sm text-gray-600">กำลังอัพโหลด... {Math.round(uploadProgress)}%</p>
            </div>
          ) : (
            <div>
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                stroke="currentColor"
                fill="none"
                viewBox="0 0 48 48"
              >
                <path
                  d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8m0-8h8m-4 4H12m16 16v-8m0 0v-8m0 8H12m16 0h8"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <p className="mt-2 text-sm text-gray-600">
                คลิกหรือลากรูปภาพมาวางที่นี่
              </p>
              <p className="text-xs text-gray-500 mt-1">
                สามารถอัพโหลดได้อีก {remainingSlots} รูป (สูงสุด {effectiveMaxImages} รูป)
                {userPlan && (
                  <span className="ml-1 text-blue-600">
                    {userPlan === 'PRO' ? '✨ Pro Plan' : '🆓 Free Plan'}
                  </span>
                )}
              </p>
            </div>
          )}
        </div>
      )}

      {!canUploadMore && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
          <p className="text-sm text-yellow-800">
            ถึงขีดจำกัดการอัพโหลดรูปภาพแล้ว ({effectiveMaxImages} รูป)
          </p>
          {userPlan === 'FREE' && (
            <p className="text-xs text-yellow-700 mt-1">
              💡 สมัคร Pro Plan เพื่ออัพโหลดได้สูงสุด 3 รูป
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default EquipmentImageUpload;

